import {
  CollectionEventContext,
  CollectionTrackingContext,
  Product,
  ProductEvent,
  SharedCollection,
  WishlistCollection
} from '../../@types/wishlist.js';
import {ProductIdentifier} from '../service/product-identifier.js';
import {WishlistService} from '../service/wishlist-service.js';
import {
  trackAddItemToCollection,
  trackCreateCollection,
  trackDeleteCollection,
  trackIsSharingAllowedToggled,
  trackRemoveItemFromCollection,
  trackRenameCollection
} from '../service/cube-tracking-layer.js';
import {ProductEventAggregate} from '../product-events/product-event-aggregate.js';
import {curatedCollectionApi} from '../service/curated-collection-api.js';

interface AbstractArrayState<TYPE, ID> {
  entities: TYPE[]
  equalsId: (entity: TYPE, id: ID) => boolean | undefined
  find: (id: ID) => TYPE | undefined
  update: (newState: Partial<TYPE>, id: ID) => void
  updateAll: (newState: TYPE[]) => void
}

export class AbstractArrayStateImpl<TYPE, ID> implements AbstractArrayState<TYPE, ID> {
  entities: TYPE[] = [];
  find = (id: ID): TYPE | undefined => {
    return this.entities.find(e => this.equalsId(e, id));
  };
  equalsId = (entity: TYPE, id: ID): boolean => {
    throw new Error(`getId not implemented, input[${entity}, ${id}]`);
  };
  update = (newState: Partial<TYPE>, id: ID): void => {
    const mergedEntities = this.entities.map(e =>
      this.equalsId(e, id) ? {...e, ...newState} : e
    );
    this.updateAll(mergedEntities);
  };
  updateAll = (newState: TYPE[]): void => {
    throw new Error(`updateAll not implemented, input[${newState}]`);
  };
}

export interface CollectionsState extends AbstractArrayState<WishlistCollection, string> {
  delete: (collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext, removeFromFavorites: boolean) => void
  add: (collection: WishlistCollection, eventContext: CollectionEventContext) => void
  putToTop: (collection: WishlistCollection) => void
  updateOnRename: (collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext) => void;
  updateOnIsSharingAllowed: (collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext) => void;
  updateOnSharedWithTopCustomers: (collection: WishlistCollection) => void;
}

export class CollectionsStateImpl extends AbstractArrayStateImpl<WishlistCollection, string> implements CollectionsState {
  equalsId = (entity: WishlistCollection, id: string): boolean => {
    return entity.id === id;
  };

  delete(collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext, removeFromFavorites: boolean): void {
    this.updateAll(this.entities.filter(c => c.id !== collection.id));
    trackDeleteCollection(collection, collectionTrackingContext, removeFromFavorites);
  }

  add(collection: WishlistCollection, eventContext: CollectionEventContext): void {
    this.updateAll([collection, ...this.entities]
      .sort((a, b) => b.created.localeCompare(a.created)));
    trackCreateCollection(collection, eventContext);
  }

  putToTop(collection: WishlistCollection): void {
    this.updateAll([this.entities.find(c => c.id === collection.id) ?? collection, ...this.entities.filter(c => c.id !== collection.id)]);
  }

  updateOnRename(collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext): void {
    this.update(collection, collection.id);
    trackRenameCollection(collection, collectionTrackingContext);
  }

  updateOnIsSharingAllowed(collection: WishlistCollection, collectionTrackingContext: CollectionTrackingContext): void {
    this.update(collection, collection.id);
    trackIsSharingAllowedToggled(collection, collectionTrackingContext);
  }

  updateOnSharedWithTopCustomers(collection: WishlistCollection): void {
    this.update(collection, collection.id);
  }
}

export interface CuratedCollectionsState {
  getAll: (endpoint: string) => Promise<SharedCollection[]>;
  getOne: (endpoint: string, id: string) => Promise<SharedCollection | undefined>;
}

export class CuratedCollectionsStateImpl extends AbstractArrayStateImpl<SharedCollection, string> implements CuratedCollectionsState {
  private initialized = false;

  private readonly initialize = async (endpoint: string) => {
    if (!this.initialized) {
      const collections = await curatedCollectionApi.getCollections(endpoint);
      this.updateAll(collections);
      this.initialized = true;
    }
  };

  equalsId = (entity: SharedCollection, id: string): boolean => {
    return entity.collection.id === id;
  };

  getOne = async (endpoint: string, id: string): Promise<SharedCollection | undefined> => {
    await this.initialize(endpoint);
    return this.find(id);
  };

  getAll = async (endpoint: string): Promise<SharedCollection[]> => {
    await this.initialize(endpoint);
    return this.entities;
  };

  updateAll = (newState: SharedCollection[]): void => {
    this.entities = newState;
  };
}

export interface ProductsState extends AbstractArrayState<Product, ProductIdentifier> {
  sharedProducts?: Product[]
  isShared: () => boolean
  getProductsToRender: (collectionId?: string) => Product[]
  updateProductsAndSharedProducts: (newProducts: Product[], newShared?: Product[]) => void
  removeFromCollection: (
    product: Product,
    collection: WishlistCollection,
    collectionEventContext: CollectionEventContext,
  ) => void
  addToCollection: (
    product: Product,
    collection: WishlistCollection,
    collectionEventContext: CollectionEventContext
  ) => void
  updateSelectedArticle: (id: ProductIdentifier, articleId: string) => void
  updateSelectedArticleBySyan: (syan: number, articleId: string) => void
  isProductInAnyCollection: (id: ProductIdentifier) => boolean
  isProductInCollection: (id: ProductIdentifier, collectionId: string) => boolean
  getProductEventAggregates: (
    unseenProductEvents: ProductEvent[],
    seenProductEvents: ProductEvent[]
  ) => { unseenProductEvents: ProductEventAggregate[], seenProductEvents: ProductEventAggregate[] }
}

export class ProductsStateImpl extends AbstractArrayStateImpl<Product, ProductIdentifier> implements ProductsState {
  sharedProducts?: Product[] = undefined;
  updateAll = (newState: Product[]): void => {
    this.updateProductsAndSharedProducts(newState, this.sharedProducts);
  };
  updateProductsAndSharedProducts = (newProducts: Product[], newShared?: Product[]): void => {
    throw new Error(`updateProductsAndSharedProducts not implemented, input[${newProducts},${newShared}]`);
  };
  equalsId = (entity: Product, id: ProductIdentifier): boolean => {
    return ProductIdentifier.createByProduct(entity).equalsProductIdentifier(id);
  };
  getProductsToRender = (collectionId?: string): Product[] => {
    if (collectionId) {
      return WishlistService.getProductsForCollectionWithDefaultSorting(this.entities, collectionId);
    } else if (this.sharedProducts) {
      return this.sharedProducts;
    } else {
      return this.entities;
    }
  };
  isShared = (): boolean => this.sharedProducts !== undefined;
  addToCollection = (
    product: Product,
    collection: WishlistCollection,
    collectionEventContext: CollectionEventContext
  ): void => {
    this.update({
      collectionReferences: [
        ...product.collectionReferences,
        {
          collectionId: collection.id,
          added: new Date().toISOString()
        }
      ]
    }, ProductIdentifier.createByProduct(product));
    trackAddItemToCollection(product, collection, collectionEventContext);
  };

  removeFromCollection(
    product: Product,
    collection: WishlistCollection,
    collectionEventContext: CollectionEventContext,
  ): void {
    this.update({
      collectionReferences: product.collectionReferences.filter(
        ref => ref.collectionId !== collection.id
      ),
    }, ProductIdentifier.createByProduct(product));
    trackRemoveItemFromCollection(product, collection, collectionEventContext, false);
  }

  updateSelectedArticle = (id: ProductIdentifier, articleId: string): void => {
    this.updateSelected((p: Product) => this.equalsId(p, id), articleId);
  };
  updateSelectedArticleBySyan = (syan: number, articleId: string): void => {
    this.updateSelected((p: Product) => p.syan === syan, articleId);
  };
  isProductInAnyCollection = (id: ProductIdentifier) => {
    const foundProduct = this.find(id);
    if (foundProduct) {
      return foundProduct?.collectionReferences.length > 0;
    }
    return false;
  };
  isProductInCollection = (id: ProductIdentifier, collectionId: string) => {
    return this.find(id)
        ?.collectionReferences.some(collectionReference => collectionReference.collectionId === collectionId)
      ?? false;
  };
  getProductEventAggregates = (
    unseenProductEvents: ProductEvent[],
    seenProductEvents: ProductEvent[]
  ) => {
    const eventMap = new Map<string, ProductEvent>();
    unseenProductEvents.forEach((event: ProductEvent) => {
      eventMap.set(ProductIdentifier.create(event.syan, event.colorId).id, event);
    });
    seenProductEvents.forEach((event: ProductEvent) => {
      eventMap.set(ProductIdentifier.create(event.syan, event.colorId).id, event);
    });

    const aggregates = new Map<string, ProductEventAggregate>();
    this.entities.forEach((product) => {
      const productId = ProductIdentifier.createByProduct(product).id;
      const event = this.findValidEvent(eventMap, product);

      if (event) {
        aggregates.set(productId, new ProductEventAggregate(product, event));
      }
    });

    return {
      unseenProductEvents: unseenProductEvents.map((event: ProductEvent) => aggregates.get(ProductIdentifier.create(event.syan, event.colorId).id))
        .filter(aggregate => aggregate != undefined),
      seenProductEvents: seenProductEvents.map((event: ProductEvent) => aggregates.get(ProductIdentifier.create(event.syan, event.colorId).id))
        .filter(aggregate => aggregate != undefined)
    };
  };

  private findValidEvent(eventMap: Map<string, ProductEvent>, product: Product): ProductEvent | undefined {
    const productId = ProductIdentifier.createByProduct(product).id;
    const event = eventMap.get(productId);
    if (!event?.eventTypes.includes('PRODUCT_CAMPAIGN_STARTED')) {
      return event;
    }
    if (product.articleId && product.articles.some(a => a.id === product.articleId && a.isInPromotion)) {
      return event;
    }
    if (!product.articleId && product.articles.some(a => a.isInPromotion)) {
      return event;
    }
    if (event.eventTypes.length === 1) {
      return undefined;
    }

    return {
      ...event,
      eventTypes: event.eventTypes.filter(t => t !== 'PRODUCT_CAMPAIGN_STARTED'),
      trackingCampaigns: event.trackingCampaigns.filter(t => t !== 'promotion')
    };
  }

  private updateSelected = (equalFn: (p: Product) => boolean, articleId: string): void => {
    const entitiesToUpdate = this.sharedProducts ? this.sharedProducts : this.entities;
    const mergedEntities = entitiesToUpdate.map(p =>
      (equalFn(p) && p.articles.find(a => a.id === articleId) != null) ? {...p, ...{articleId}} : p
    );
    const newProducts = this.sharedProducts ? this.entities : mergedEntities;
    const newShared = this.sharedProducts ? mergedEntities : this.sharedProducts;
    this.updateProductsAndSharedProducts(newProducts, newShared);
  };
}
